import type { StockLot } from './api';
import { fmtDate, type State } from './ui';

/**
 * The words and colours the store is drawn in.
 *
 * Shared by the screen and its dialogs rather than defined in either, so a lot
 * described as expiring in a table row cannot be described differently in the
 * dialog that opens from it.
 */

export const KIND_LABEL: Record<string, string> = {
  COMPONENT: 'Component',
  STANDARD_PART: 'Standard part',
  RAW_MATERIAL: 'Raw material',
  CONSUMABLE: 'Consumable',
};

export const LOCATION_KIND_LABEL: Record<string, string> = {
  SERVICEABLE: 'Serviceable',
  QUARANTINE: 'Quarantine',
  UNSERVICEABLE: 'Unserviceable',
  SCRAP: 'Scrap',
};

/**
 * The condition tag, as a chip.
 *
 * Green serviceable, yellow quarantine, red unserviceable. Convention rather
 * than regulation — the rules ask for identification and segregation and never
 * name a colour — but it is the scheme in common European use, and the API
 * decides which one a lot wears so the label PDF and the screen agree.
 */
export const TAG_STYLE: Record<string, string> = {
  GREEN: 'background:rgba(28,122,62,.14);color:#1c7a3e;border-color:rgba(28,122,62,.4)',
  YELLOW: 'background:rgba(217,164,6,.16);color:#8a6800;border-color:rgba(217,164,6,.45)',
  RED: 'background:rgba(179,38,30,.14);color:#b3261e;border-color:rgba(179,38,30,.4)',
};

export const MOVEMENT_LABEL: Record<string, string> = {
  RECEIPT: 'Received',
  INSPECTION_PASS: 'Incoming inspection — passed',
  INSPECTION_FAIL: 'Incoming inspection — failed',
  ISSUE: 'Issued',
  RETURN: 'Returned to store',
  TRANSFER: 'Moved',
  OPEN: 'Opened',
  RESERVE: 'Removed from an aircraft',
  REFIT: 'Refitted',
  RELEASE_RESERVATION: 'Reservation released',
  CONDEMN: 'Condemned',
  MUTILATE: 'Mutilated',
  RETURN_TO_SUPPLIER: 'Returned to the supplier',
  STOCK_CHECK: 'Stock check',
  CORRECTION: 'Corrected after a count',
};

export function lotState(l: StockLot): State {
  if (l.expired || l.state === 'UNSERVICEABLE' || l.state === 'UNSALVAGEABLE') return 'OVERDUE';
  if (l.state === 'QUARANTINE' || (l.daysToExpiry !== null && l.daysToExpiry <= 90)) return 'DUE_SOON';
  if (l.state === 'SERVICEABLE' || l.state === 'RESERVED') return 'OK';
  return 'NA';
}

/** Which of the two shelf clocks produced the date, said in words. */
export function expiryText(l: StockLot): string {
  if (!l.effectiveExpiry) return 'no shelf life';
  const when = fmtDate(l.effectiveExpiry);
  const from = l.expiryDriver === 'OPENED' ? ' (from opening)' : '';
  if (l.expired) return `expired ${when}`;
  if (l.daysToExpiry !== null && l.daysToExpiry <= 90) return `${l.daysToExpiry} d left · ${when}${from}`;
  return `${when}${from}`;
}
